"use client"

import { cva, type VariantProps } from "class-variance-authority"
import { useMemo } from "react"
import { InfoIcon } from "lucide-react"
import { Button } from "~/components/ui/button"
import { Label } from "~/components/ui/label"
import { Separator } from "~/components/ui/separator"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger
} from "~/components/ui/tooltip"
import { cn } from "~/lib/utils"

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
	return (
		<fieldset
			data-slot="field-set"
			className={cn(
				"flex flex-col gap-6",
				"has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3",
				className
			)}
			{...props}
		/>
	)
}

function FieldLegend({
	className,
	variant = "legend",
	...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
	return (
		<legend
			data-slot="field-legend"
			data-variant={variant}
			className={cn(
				"mb-3 font-medium",
				"data-[variant=legend]:text-base",
				"data-[variant=label]:text-sm",
				className
			)}
			{...props}
		/>
	)
}

function FieldGroup({
	className,
	variant = "default",
	...props
}: React.ComponentProps<"div"> & { variant?: "default" | "surface" }) {
	return (
		<div
			data-slot="field-group"
			data-variant={variant}
			className={cn(
				"group/field-group @container/field-group flex w-full flex-col",
				variant === "default"
					? "gap-7 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4"
					: "gap-0 overflow-hidden rounded-lg bg-background",
				className
			)}
			{...props}
		/>
	)
}

const fieldVariants = cva(
	"group/field flex w-full data-[invalid=true]:text-destructive",
	{
		variants: {
			orientation: {
				vertical: [
					"flex-col gap-2 [&>:not([data-slot=switch])]:w-full [&>.sr-only]:w-auto"
				],
				horizontal: [
					"flex-row items-center gap-3",
					"*:data-[slot=field-label]:flex-auto",
					"has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
				],
				responsive: [
					"flex-col gap-2 @md/field-group:flex-row @md/field-group:items-center @md/field-group:gap-3 [&>:not([data-slot=switch])]:w-full @md/field-group:[&>:not([data-slot=switch])]:w-auto [&>.sr-only]:w-auto",
					"@md/field-group:*:data-[slot=field-label]:flex-auto",
					"@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
				]
			}
		},
		defaultVariants: {
			orientation: "vertical"
		}
	}
)

function Field({
	className,
	orientation = "vertical",
	...props
}: React.ComponentProps<"fieldset"> & VariantProps<typeof fieldVariants>) {
	return (
		<fieldset
			data-slot="field"
			data-orientation={orientation}
			className={cn(
				"m-0 min-w-0 border-0 p-0",
				fieldVariants({ orientation }),
				className
			)}
			{...props}
		/>
	)
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="field-content"
			className={cn(
				"group/field-content flex flex-1 flex-col gap-1.5 leading-snug",
				className
			)}
			{...props}
		/>
	)
}

function FieldLabel({
	className,
	variant = "default",
	tooltip,
	...props
}: React.ComponentProps<typeof Label> & {
	variant?: "default" | "choice" | "description"
	tooltip?: React.ReactNode
}) {
	const content = (
		<Label
			data-slot="field-label"
			className={cn(
				"group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50",
				"has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-4",
				"has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5 dark:has-data-[state=checked]:bg-primary/10",
				variant === "description" && "cursor-pointer font-normal leading-5",
				variant === "choice" &&
					"group flex w-full cursor-pointer flex-col items-start gap-5 rounded-xl border border-button-edge bg-background p-4 shadow-button has-data-[state=checked]:border-orbit has-data-[state=checked]:bg-orbit/5 hover:bg-active",
				className
			)}
			{...props}
		/>
	)
	return tooltip ? (
		<span className="inline-flex min-w-0 items-center gap-1.5">
			{content}
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						type="button"
						variant="none"
						size="icon-xs"
						aria-label="More information"
					>
						<InfoIcon className="size-4 text-icons" />
					</Button>
				</TooltipTrigger>
				<TooltipContent>{tooltip}</TooltipContent>
			</Tooltip>
		</span>
	) : (
		content
	)
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="field-label"
			className={cn(
				"flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50",
				className
			)}
			{...props}
		/>
	)
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
	return (
		<p
			data-slot="field-description"
			className={cn(
				"text-sm leading-normal font-normal text-muted-foreground group-has-[[data-orientation=horizontal]]/field:text-balance",
				"last:mt-0 nth-last-2:-mt-1 [[data-variant=legend]+&]:-mt-1.5",
				"[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-orbit-text",
				className
			)}
			{...props}
		/>
	)
}

function FieldSeparator({
	children,
	className,
	...props
}: React.ComponentProps<"div"> & {
	children?: React.ReactNode
}) {
	return (
		<div
			data-slot="field-separator"
			data-content={!!children}
			className={cn(
				"relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2",
				className
			)}
			{...props}
		>
			<Separator className="absolute inset-0 top-1/2" />
			{children && (
				<span
					className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground"
					data-slot="field-separator-content"
				>
					{children}
				</span>
			)}
		</div>
	)
}

function FieldError({
	className,
	children,
	errors,
	...props
}: React.ComponentProps<"div"> & {
	errors?: Array<{ message?: string } | undefined>
}) {
	const content = useMemo(() => {
		if (children) {
			return children
		}

		if (!errors?.length) {
			return null
		}

		const uniqueErrors = [
			...new Map(errors.map((error) => [error?.message, error])).values()
		]

		if (uniqueErrors?.length === 1) {
			return uniqueErrors[0]?.message
		}

		return (
			<ul className="ml-4 flex list-disc flex-col gap-1">
				{uniqueErrors.map(
					(error, index) =>
						error?.message && <li key={index}>{error.message}</li>
				)}
			</ul>
		)
	}, [children, errors])

	if (!content) {
		return null
	}

	return (
		<div
			role="alert"
			data-slot="field-error"
			className={cn("text-sm font-normal text-destructive", className)}
			{...props}
		>
			{content}
		</div>
	)
}

function FieldRow({
	label,
	description,
	tooltip,
	children,
	htmlFor,
	variant = "default"
}: {
	label: React.ReactNode
	description?: React.ReactNode
	tooltip?: React.ReactNode
	children: React.ReactNode
	htmlFor?: string
	variant?: "default" | "surface" | "divided"
}) {
	return (
		<Field
			orientation="horizontal"
			className={cn(
				"justify-between",
				variant === "surface" &&
					"min-h-14 flex-wrap rounded-lg bg-background p-4",
				variant === "divided" &&
					"min-h-14 border-b border-stroke/50 py-2 last:border-b-0"
			)}
		>
			<FieldContent>
				<FieldLabel htmlFor={htmlFor} tooltip={tooltip}>
					{label}
				</FieldLabel>
				{description && <FieldDescription>{description}</FieldDescription>}
			</FieldContent>
			{children}
		</Field>
	)
}

export {
	FieldRow,
	Field,
	FieldContent,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldLegend,
	FieldSeparator,
	FieldSet,
	FieldTitle
}
